/**
 * @copyright: 2020 NTWIST
 * @Author: NTWIST
 * @Date: 2021-03-15 11:07:50
 * @Last Modified by: Pradeep Chandra
 * @Last Modified time: 2022-06-21 20:14:04
 */

import axios from "axios";
import { toaster, Message } from "rsuite";
import React from "react";
import BaseURL from "../api-common";

export async function createArchive(endUserSession, clientPathName) {
    try {
        let url =
            BaseURL.getBaseURL() +
            clientPathName +
            "/databaseArchiveManagement/createArchive";

        let res = await axios({
            method: "post",
            url: url,
            withCredentials: true,
            data: {}
        });

        if (res.data.msg === "UNAUTHORIZED") {
            endUserSession();
            return;
        } else if (res.data.statusId === "ERROR") {
            throw "The server has encountered an error.";
        } else if (!res.data.isValidData) {
            throw "The request is invalid.";
        } else if (!res.data.archiveCreated) {
            throw "The server has encountered an error.";
        }

        toaster.push(
            <Message type="success" showIcon closable duration={10000}>
                {<text>Success</text>}
            </Message>
        );
        return true;
    } catch (e) {
        toaster.push(
            <Message type="error" showIcon closable duration={10000}>
                {
                    <text>
                        Could not create archive.
                        <br />
                        Error: {e.toString()}
                    </text>
                }
            </Message>
        );
        return false;
    }
}

export async function getArchiveDetails(endUserSession, clientPathName) {
    try {
        let url =
            BaseURL.getBaseURL() +
            clientPathName +
            "/databaseArchiveManagement/getArchiveDetails";

        let res = await axios({
            method: "get",
            url: url,
            withCredentials: true,
            data: {}
        });

        if (res.data.msg === "UNAUTHORIZED") {
            endUserSession();
            return;
        } else if (res.data.statusId === "ERROR") {
            throw "The server has encountered an error.";
        } else if (!res.data.isValidData) {
            throw "The request is invalid.";
        } else if (!res.data.archiveDetails) {
            throw "The server has encountered an error.";
        }

        return res.data.archiveDetails;
    } catch (e) {
        toaster.push(
            <Message type="error" showIcon closable duration={10000}>
                {
                    <text>
                        Could not get archive details.
                        <br />
                        Error: {e.toString()}
                    </text>
                }
            </Message>
        );
        return null;
    }
}

export async function downloadArchive(endUserSession, clientPathName, data) {
    try {
        let url =
            BaseURL.getBaseURL() +
            clientPathName +
            "/databaseArchiveManagement/downloadArchive";

        let res = await axios({
            method: "post",
            url: url,
            withCredentials: true,
            data: data,
            responseType: "blob", // Important for file downloads
            validateStatus: function () {
                return true; // Prevent axios from throwing status error
            }
        });

        if (res.status === 300) {
            throw "The request is invalid.";
        }

        // Create a tag element and assign blob file to it. Then programmatically click it.
        const downloadUrl = window.URL.createObjectURL(new Blob([res.data]));
        const downloadElement = document.createElement("a");
        downloadElement.href = downloadUrl;
        downloadElement.setAttribute("download", data.fileName + ".archive");
        document.body.appendChild(downloadElement);
        downloadElement.click();
        return true;
    } catch (e) {
        toaster.push(
            <Message type="error" showIcon closable duration={10000}>
                {
                    <text>
                        Could not start archive download.
                        <br />
                        Error: {e.toString()}
                    </text>
                }
            </Message>
        );
        return false;
    }
}

export async function restoreArchive(endUserSession, clientPathName, data) {
    try {
        let url =
            BaseURL.getBaseURL() +
            clientPathName +
            "/databaseArchiveManagement/restoreArchive";

        let res = await axios({
            method: "post",
            url: url,
            withCredentials: true,
            data: data,
            timeout: 6000000
        });

        if (res.data.msg === "UNAUTHORIZED") {
            endUserSession();
            return;
        } else if (res.data.statusId === "ERROR") {
            throw "The server has encountered an error.";
        } else if (!res.data.isValidData) {
            throw "The request is invalid.";
        } else if (!res.data.archiveRestored) {
            throw "The server has encountered an error.";
        }

        toaster.push(
            <Message type="success" showIcon closable duration={10000}>
                {<text>Success</text>}
            </Message>
        );
        return res.data.archiveRestored;
    } catch (e) {
        toaster.push(
            <Message type="error" showIcon closable duration={10000}>
                {
                    <text>
                        Could not restore archive.
                        <br />
                        Error: {e.toString()}
                    </text>
                }
            </Message>
        );
        return false;
    }
}

export async function deleteArchive(endUserSession, clientPathName, data) {
    try {
        let url =
            BaseURL.getBaseURL() +
            clientPathName +
            "/databaseArchiveManagement/deleteArchive";

        let res = await axios({
            method: "post",
            url: url,
            withCredentials: true,
            data: data
        });

        if (res.data.msg === "UNAUTHORIZED") {
            endUserSession();
            return;
        } else if (res.data.statusId === "ERROR") {
            throw "The server has encountered an error.";
        } else if (!res.data.isValidData) {
            throw "The request is invalid.";
        } else if (!res.data.archiveDeleted) {
            throw "The server has encountered an error.";
        }

        toaster.push(
            <Message type="success" showIcon closable duration={10000}>
                {<text>Success</text>}
            </Message>
        );
        return true;
    } catch (e) {
        toaster.push(
            <Message type="error" showIcon closable duration={10000}>
                {
                    <text>
                        Could not delete archive.
                        <br />
                        Error: {e.toString()}
                    </text>
                }
            </Message>
        );
        return false;
    }
}
